<template>
  <Form
    :validation-schema="schema"
    :initial-values="props.initialValues"
    @submit="handleForm"
  >
    <div class="py-4 px-4 sm:px-8 bg-[#F9FAFC] w-full mt-8 flex flex-col gap-y-8 pt-8 rounded-md">
      <!-- Personal Information Section -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Personal Information</h4>
        <div class="w-full h-[1px] bg-gray-200"></div>
        <div class="grid gap-4 mt-2 md:grid-cols-2">
          <!-- Full name -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="fullName">Full name</label>
            <Field
              name="fullName"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors }"
            >
              <input
                v-bind="field"
                type="text"
                placeholder="Insert your full name..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                :disabled="loading"
              />
            </Field>
            <ErrorMessage name="fullName" class="text-sm text-red-500" />
          </div>
          <!-- Email -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="email">Email</label>
            <Field
              name="email"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors, handleChange }"
            >
              <input
                v-bind="field"
                type="text"
                placeholder="Insert an email..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                :disabled="loading"
                @blur="trimEmailField(field.value, handleChange)"
              />
            </Field>
            <ErrorMessage name="email" class="text-sm text-red-500" />
          </div>
          <!-- Contact number -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="contactNumber">Contact number</label>
            <IntlTelInput
              class="w-full"
              :class="{ 'ring-red-500 border-red-500': hasBeenSubmitted && !isPhoneValid }"
              @changeNumber="phoneNumber = $event"
              @changeValidity="isPhoneValid = $event" 
              @changeErrorCode="phoneErrorCode = $event"
              :options="{
                initialCountry: 'us',
              }"
              
            />
            <span v-if="hasBeenSubmitted && !isPhoneValid" class="text-sm text-red-500">
              {{ getPhoneErrorMessage() }}
            </span>
          </div>
          <!-- Location -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="location">Location</label>
            <Field
              name="location"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors }"
            >
              <div class="relative">
                <select
                  v-bind="field"
                  class="w-full appearance-none pr-8"
                  :class="{ 'ring-red-500 border-red-500': errors[0] }"
                  :disabled="loading"
                >
                  <option value="">Select a country...</option>
                  <option
                    v-for="country in countries"
                    :key="country"
                    :value="country"
                  >
                    {{ country }}
                  </option>
                </select>
                <div
                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                    class="w-4 h-4"
                  >
                    <path
                      d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                    />
                  </svg>
                </div>
              </div>
            </Field>
            <ErrorMessage name="location" class="text-sm text-red-500" />
          </div>
        </div>
      </div>

      <!-- Professional Background -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Professional Background</h4>
        <div class="w-full h-[1px] bg-gray-200"></div>
        <div class="grid gap-4 mt-2">
          <!-- Years of experience -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979] text-base sm:text-lg" for="yearsExperience">
              Years of experience in mixing and mastering
            </label>
            <Field
              name="yearsExperience"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors }"
            >
              <input
                v-bind="field"
                min="0"
                type="number"
                placeholder="Insert years of experience"
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                style="appearance: textfield"
                :disabled="loading"
                @keypress="
                  (e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }
                "
                @paste="
                  (e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text');
                    if (/^[0-9]+$/.test(text)) {
                      e.target.value = text;
                    }
                  }
                "
              />
            </Field>
            <ErrorMessage name="yearsExperience" class="text-sm text-red-500" />
          </div>

          <!-- DAWs Used -->
          <div class="flex flex-col gap-y-1">
            <label class="font-medium text-[#7B7979]">Preferred DAWs and software</label>
            <Field
              name="dawsUsed"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors }"
            >
              <input
                v-bind="field"
                type="text"
                placeholder="List your preferred DAWs and audio processing software..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                :disabled="loading"
              />
            </Field>
            <ErrorMessage name="dawsUsed" class="text-sm text-red-500" />
          </div>

          <!-- Portfolio Links -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]">Portfolio Links</label>
            <div class="flex flex-col gap-y-4">
              <!-- Required primary portfolio link -->
              <div class="relative">
                <Field
                  name="primaryPortfolioUrl"
                  v-slot="{ field: fieldProps, errors }"
                >
                  <input
                    v-bind="fieldProps"
                    type="text"
                    placeholder="Insert an URL to your main portfolio..."
                    class="w-full"
                    :class="{ 'ring-red-500 border-red-500': errors[0] }"
                    :disabled="loading"
                  />
                  <ErrorMessage
                    name="primaryPortfolioUrl"
                    class="text-sm text-red-500"
                  />
                </Field>
              </div>

                <!-- Additional portfolio links -->
                <FieldArray name="additionalPortfolioLinks" v-slot="{ fields, push, remove }">
                <div v-for="(field, index) in fields" :key="field.key">
                  <div class="flex gap-x-2">
                  <div class="grow">
                    <Field
                    :name="`additionalPortfolioLinks[${index}].url`"
                    v-slot="{ field: fieldProps, errors }"
                    >
                    <div>
                      <input
                      v-bind="fieldProps"
                      type="text"
                      :placeholder="`Insert an additional portfolio link...`"
                      class="w-full"
                      :class="{ 'ring-red-500 border-red-500': errors[0] }"
                      :disabled="loading"
                      />
                    </div>
                    </Field>
                  </div>
                  <button
                    type="button"
                    class="btn-form"
                    @click="() => remove(index)"
                    :disabled="loading"
                  >
                    -
                  </button>
                  </div>
                  <ErrorMessage
                  :name="`additionalPortfolioLinks[${index}].url`"
                  class="text-sm text-red-500 mt-1"
                  />
                </div>
                <button
                  v-if="fields.length < 2"
                  type="button"
                  class="w-full btn-form self-start py-2 mt-2"
                  @click="() => push({ url: '' })"
                  :disabled="fields.length >= 2 || loading"
                >
                  + Add additional portfolio link
                </button>
                </FieldArray>
            </div>
          </div>
        </div>
      </div>

      <!-- Technical Setup Confirmation -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Technical Setup Confirmation</h4>
        <div class="w-full h-[1px] bg-gray-200"></div>

        <div class="flex items-start gap-x-0 mt-4 flex-col sm:items-center gap-y-2 sm:flex-row sm:gap-y-2 sm:gap-x-2">
          <div class="sm:contents flex items-start w-full">
            <Field
              name="internetConnection"
              type="checkbox"
              class="h-5 w-full max-w-[20px] cursor-pointer transition-all appearance-none rounded-md border border-slate-300 checked:bg-principal-500 checked:border-principal-500"
              id="internetConnection"
              value="true"
              :disabled="loading"
            >
            </Field>
            <label
              for="internetConnection"
              class="text-sm font-medium text-gray-900 cursor-pointer ms-1"
            >
              I have reliable internet connection
            </label>
          </div>
          <ErrorMessage name="internetConnection" class="text-sm text-red-500 " />
        </div>

        <div class="flex items-start gap-x-0 mt-4 flex-col sm:items-center gap-y-2 sm:flex-row sm:gap-y-2 sm:gap-x-2">
          <div class="sm:contents flex items-start w-full">
            <Field
              name="hardwareSoftwareSetup"
              type="checkbox"
              class="h-5 w-full max-w-[20px] cursor-pointer transition-all appearance-none rounded-md border border-slate-300 checked:bg-principal-500 checked:border-principal-500"
              id="hardwareSoftwareSetup"
              value="true"
              :disabled="loading"
            >
            </Field>
            <label
              for="hardwareSoftwareSetup"
              class="text-sm font-medium text-gray-900 cursor-pointer ms-1"
            >
              I have the necessary hardware/software setup
            </label>
          </div>
          <ErrorMessage name="hardwareSoftwareSetup" class="text-sm text-red-500" />
        </div>
      </div>

      <!-- Availability Section -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Availability</h4>
        <div class="w-full h-[1px] bg-gray-200 mb-2"></div>
        <label class="font-medium text-[#7B7979]" for="availability"
          >Expected Monthly Hours</label
        >
        <div class="relative flex flex-col w-full mt-1">
          <Field
            name="availability"
            :validateOnInput="hasBeenSubmitted"
            v-slot="{ field, errors }"
          >
            <div class="relative w-full">
              <select 
                v-bind="field" 
                class="w-full appearance-none pr-8" 
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                :disabled="loading"
              >
                <option value="">Select availability</option>
                <option value="10 hours">10 hours</option>
                <option value="10-20 hours">10-20 hours</option>
                <option value="20-30 hours">20-30 hours</option>
                <option value="30-40 hours">30-40 hours</option>
                <option value="40+ hours">40+ hours</option>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                  class="w-4 h-4"
                >
                  <path
                    d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                  />
                </svg>
              </div>
            </div>
          </Field>
          <ErrorMessage name="availability" class="text-sm text-red-500" />
        </div>
      </div>
    </div>
    <div class="w-full flex justify-end py-8 px-2 sm:px-0">
      <ButtonComponent
        @click="hasBeenSubmitted = true"
        type="submit"
        :class="['btn-primary', 'flex', 'items-center', 'gap-y-2']"
        text="Next"
        :loading="loading"
        :disabled="loading"
      >
        Next
      </ButtonComponent>
    </div>
  </Form>

  <CodeOTP v-if="viewCodeOTP" :email="emailEntered" @next="onSubmit" @back="deleteSectionCodeOTP"/>
  <MessageComponent/>
</template>

<script setup>
/*eslint-disable */
import CodeOTP from "@/shared/component/CodeOTP.vue";
import MessageComponent from "@/shared/component/MessageComponent.vue";
import { useMessage } from "@/shared/services/messageServices";
import { Field, Form, ErrorMessage, FieldArray } from "vee-validate";
import { defineProps, defineEmits, onMounted, ref } from "vue";
import * as yup from "yup";
import axios from "axios"; 
import ButtonComponent from "@/shared/component/ButtonComponent.vue";
import { countries } from "@/shared/utils/countries.js";
import {trimEmailField} from "@/shared/utils/helper.js";
import IntlTelInput from "intl-tel-input/vueWithUtils";
import "intl-tel-input/styles";

const messagesService = useMessage();

const hasBeenSubmitted = ref(false);
const viewCodeOTP = ref(false);
const emailEntered = ref("");
const valuesForm = ref({});
const loading = ref(false);

const props = defineProps({
  initialValues: Object,
});

const emit = defineEmits(["next"]);

const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)*\.[a-z]{2,})(\/[\w@:%_+.~#?&//=()-]*)?$/i;

// Phone validation
const phoneNumber = ref("");
const isPhoneValid = ref(false);
const phoneErrorCode = ref(null);

/* function trimEmailField(value, handleChange) {
  if (value && typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue !== value) {
      handleChange(trimmedValue);
    }
  }
} */

function handleEmailInput(event, handleChange) {
  console.log("Email input triggered");
  
  let value = event.target.value.trim();
  
  // Update the field value
  handleChange(value);
  
  // Debounced email check
  debouncedEmailCheck(value);
}

function getPhoneErrorMessage() {
  if (!phoneNumber.value) return "Contact number is required";
  if (!isPhoneValid.value) return "Please enter a valid phone number";
  return "";
}

// Update schema to remove phone validation (handled by IntlTelInput)
const schema = yup.object().shape({
  fullName: yup
    .string()
    .required("Full name is required")
    .min(2, "Name must be at least 2 characters"),
  email: yup.string().required("Email is required").email("Invalid email format"),
  location: yup.string().required("Location is required"),
  yearsExperience: yup
    .number()
    .typeError("Must be a number")
    .required("Years of experience is required")
    .min(0, "Years of experience must be positive")
    .transform((value) => (isNaN(value) ? undefined : value)),
  dawsUsed: yup
    .string()
    .required("DAWs and software information is required")
    .min(2, "Please provide more detail about your tools"),
  primaryPortfolioUrl: yup
    .string()
    .required("Main portfolio URL is required")
    .matches(urlRegex, "Must be a valid URL"),
  additionalPortfolioLinks: yup.array().of(
    yup.object().shape({
      url: yup
        .string()
        .matches(urlRegex, "Must be a valid URL")
        .nullable(),
    })
  ),
  internetConnection: yup
    .boolean()
    .required("This field is required")
    .oneOf([true], "This field is required"),
  hardwareSoftwareSetup: yup
    .boolean()
    .required("This field is required")
    .oneOf([true], "This field is required"),
  availability: yup.string() // Optional field
});

async function handleForm(values) {
  // Check phone validity
  if (!phoneNumber.value || !isPhoneValid.value) {
    hasBeenSubmitted.value = true; // Trigger validation display
    return;
  }
  
  emailEntered.value = values.email;
  const transformedValues = { ...values };

  // Add phone number from IntlTelInput
  transformedValues.contactNumber = phoneNumber.value;
  
  const isEmailPresent = await verifyEmail(values.email);
  if (!isEmailPresent) {
    return;
  }  
  
  // Transform portfolio links structure
  const primaryUrl = transformedValues.primaryPortfolioUrl;
  const additionalUrls = transformedValues.additionalPortfolioLinks?.map(link => link.url).filter(Boolean) || [];
  
  // Create array with primary URL and additional URLs
  transformedValues.portfolioLinks = [primaryUrl, ...additionalUrls].filter(Boolean);
  
  // Remove the original fields
  delete transformedValues.primaryPortfolioUrl;
  delete transformedValues.additionalPortfolioLinks;
  
  delete valuesForm.value.email;
  valuesForm.value = transformedValues;

  viewSectionCodeOTP(transformedValues);
}

async function verifyEmail(email) {
  loading.value = true; 
  if (props.initialValues.email != emailEntered.value) {
    try {
      await axios.post(`${process.env.VUE_APP_ENDPOINT_API}/work-with-us/verify-form`, {
        email: email,
        role: props.initialValues.role
      });
      return true;
    } catch (error) {
      messagesService.error(
        "An email is already in our platform. Please try with another email"
      );
      return false;
    }finally{
      loading.value = false;
    }
  }
}

function viewSectionCodeOTP() {
  if(props.initialValues.email != emailEntered.value) {
    viewCodeOTP.value = true;
  }
  else {
    emit("next", valuesForm.value);
  }
}

function deleteSectionCodeOTP() {
  viewCodeOTP.value = false;
}

function onSubmit() {
  valuesForm.value.email = emailEntered.value;
  emit("next", valuesForm.value);
}

onMounted(() => {
  window.scrollTo({ top: 1, behavior: "smooth" });
});
</script>
